import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { SchoolStudentFeatureKey } from '../../modules/platform/types/featureAccess';
import { useFeatureAccess } from '../../contexts/FeatureAccessContext';
import { ROUTES } from '../../utils/constants';
import { Spinner } from '../ui/Spinner';

interface StudentSchoolFeatureGateProps {
  featureKey: SchoolStudentFeatureKey;
  children: React.ReactNode;
}

function FeatureDisabledNotice({ featureKey }: { featureKey: SchoolStudentFeatureKey }) {
  const message =
    featureKey === 'ibook_reader_enabled'
      ? 'Digital Books is not enabled for your school. Ask your school administrator or DevTeam to enable iBook reader in school settings.'
      : 'This feature is not enabled for your school.';

  return (
    <div className="mx-auto max-w-lg p-8 text-center">
      <p className="text-gray-700 dark:text-gray-300" role="status">
        {message}
      </p>
      <Link to={ROUTES.STUDENT_DASHBOARD} className="mt-4 inline-block text-sm font-medium text-app-primary hover:underline">
        Back to dashboard
      </Link>
    </div>
  );
}

/**
 * For student routes gated by school_feature_access.
 * Waits for FeatureAccessProvider first fetch; shows disabled notice for iBooks instead of silent redirect.
 */
export const StudentSchoolFeatureGate: React.FC<StudentSchoolFeatureGateProps> = ({ featureKey, children }) => {
  const { loading, isSchoolFeatureEnabled } = useFeatureAccess();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isSchoolFeatureEnabled(featureKey)) {
    if (featureKey === 'ibook_reader_enabled') {
      return <FeatureDisabledNotice featureKey={featureKey} />;
    }
    return <Navigate to={ROUTES.STUDENT_DASHBOARD} replace />;
  }

  return <>{children}</>;
};
